// Optimistic overlay for offline-queued clock events.
//
// While a clock-in/clock-out sits in the offline queue the server still
// reports the old shift state, so every surface that derives "am I clocked
// in?" from server data would tell the caregiver to clock in again. This
// module overlays queued-but-unsent events onto a shift object so buttons,
// status chips and the current-shift banner reflect what the caregiver
// actually did. Overlaid shifts carry `_pendingClockIn` / `_pendingClockOut`
// / `_pendingSync` markers for "Pending sync" badges.

import { getClockEventsForShift } from "./offlineQueue";

export function pendingEventsForShift(shiftId) {
  if (shiftId === null || shiftId === undefined) {
    return { pendingClockIn: false, pendingClockOut: false, events: [] };
  }
  const events = getClockEventsForShift(shiftId);
  return {
    pendingClockIn: events.some((e) => e.type === "clock-in"),
    pendingClockOut: events.some((e) => e.type === "clock-out"),
    events,
  };
}

/**
 * Returns a copy of the shift with queued clock events applied (or the
 * original object when nothing is pending).
 */
export function applyPendingClockEvents(shift, userId) {
  if (!shift || shift.id === undefined || shift.id === null) return shift;
  const { pendingClockIn, pendingClockOut, events } = pendingEventsForShift(
    shift.id,
  );
  if (!pendingClockIn && !pendingClockOut) return shift;

  const clockInEvent = events.find((e) => e.type === "clock-in");
  const clockOutEvent = events.find((e) => e.type === "clock-out");
  const overlaid = { ...shift };

  // Patch this caregiver's clock-times row so the group/side-actor status
  // helpers (utils/shiftStatus.js) see the queued state too.
  const clockTimes = [...(shift.caregiverClockTimes || [])];
  const idx = clockTimes.findIndex(
    (ct) => Number(ct.id_users) === Number(userId),
  );
  const row =
    idx >= 0
      ? { ...clockTimes[idx] }
      : { id_users: Number(userId), clock_in: null, clock_out: null };
  if (pendingClockIn && !row.clock_in) {
    row.clock_in = clockInEvent.payload?.utcTime || clockInEvent.timestamp;
  }
  if (pendingClockOut && !row.clock_out) {
    row.clock_out = clockOutEvent.payload?.utcTime || clockOutEvent.timestamp;
  }
  if (idx >= 0) clockTimes.splice(idx, 1, row);
  else clockTimes.push(row);
  overlaid.caregiverClockTimes = clockTimes;

  const isGroup = (shift.shift_type || "1-on-1") === "Group";
  const isSideActor = !isGroup && shift.my_role && shift.my_role !== "primary";

  if (!isGroup && !isSideActor) {
    if (pendingClockOut) {
      overlaid.shift_status = "Clocked Out";
      overlaid.clock_out =
        overlaid.clock_out ||
        clockOutEvent.payload?.utcTime ||
        clockOutEvent.timestamp;
    } else if (pendingClockIn) {
      overlaid.shift_status = "Clocked In";
      overlaid.clock_in =
        overlaid.clock_in ||
        clockInEvent.payload?.utcTime ||
        clockInEvent.timestamp;
    }
  }
  if (shift.my_clock_status) {
    if (pendingClockOut) overlaid.my_clock_status = "Clocked Out";
    else if (pendingClockIn) overlaid.my_clock_status = "Clocked In";
  }

  if (pendingClockIn) {
    overlaid._pendingClockIn = true;
    // A queued clock-in on a potential shift means it's no longer "potential"
    // from the caregiver's point of view — without this the status helpers
    // would keep reporting "scheduled".
    overlaid.isPotential = false;
  }
  if (pendingClockOut) overlaid._pendingClockOut = true;
  overlaid._pendingSync = true;
  return overlaid;
}
